import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import {
  RefundReason,
  RefundType,
} from '@Encore-FastStartup-Phase-Out/encore-shared';

import { Colors } from '../../theme/colors';
import { useStore } from '../../hooks/useStore';
import { refundReasonToString, sanitizeAmount } from '../../utils/string';

interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RefundModal = ({ isOpen, onClose }: RefundModalProps) => {
  const {
    orderStore: {
      displayedListingOrder,
      getRefundRemainingPrice,
      refundRemainingPrice,
      handleRefund,
    },
  } = useStore();
  const toast = useToast();
  const bgColor = useColorModeValue(
    Colors.light.background,
    Colors.dark.background,
  );

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<RefundReason | undefined>(undefined);
  const [touchedReason, setTouchedReason] = useState(false);
  const [shouldShowAmountError, setShouldShowAmountError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRefundType, setSelectedRefundType] = useState<RefundType>(
    RefundType.ItemPrice,
  );

  const isRefundAmountZero = refundRemainingPrice === 0;

  const refundTypeOptions = [
    { label: 'Item Price', value: RefundType.ItemPrice },
    { label: 'Shipping Price', value: RefundType.ShippingPrice },
    { label: 'Processing Fee', value: RefundType.ProcessingFee },
  ];

  const onConfirmRefund = async (
    amount: number,
    reason: RefundReason,
    type: RefundType,
  ) => {
    try {
      setLoading(true);
      await handleRefund(
        reason,
        type,
        type !== RefundType.ProcessingFee ? amount : undefined,
      );

      toast({
        title: 'Success',
        description: 'Refund created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to create refund',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const isAmountValid = () => {
    const value = parseFloat(amount);
    return (
      /^\d+(\.\d{1,2})?$/.test(amount) &&
      !isNaN(value) &&
      value > 0 &&
      value <= refundRemainingPrice
    );
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeAmount(e.target.value);
    setAmount(sanitized);
  };

  const handleAmountBlur = () => {
    setShouldShowAmountError(!isAmountValid());
  };

  const handleReasonBlur = () => {
    setTouchedReason(true);
  };

  const handleConfirm = async () => {
    if (refundRemainingPrice === 0) {
      toast({
        title: 'Error',
        description: 'Refund amount is zero. Nothing to refund.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setTouchedReason(true);

    const needsAmount = selectedRefundType !== RefundType.ProcessingFee;
    const validAmount = !needsAmount || isAmountValid();

    if (validAmount && reason && selectedRefundType) {
      await onConfirmRefund(parseFloat(amount), reason, selectedRefundType);
      handleClose();
    } else {
      setShouldShowAmountError(!validAmount);
    }
  };

  const handleClose = () => {
    onClose();
    setAmount('');
    setReason(undefined);
    setTouchedReason(false);
    setShouldShowAmountError(false);
  };

  useEffect(() => {
    (async () => {
      await getRefundRemainingPrice(selectedRefundType);
    })();
  }, [displayedListingOrder, selectedRefundType]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size={'lg'}>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Refund Order</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb={4}>
            <FormLabel>Refund Type</FormLabel>
            <Select
              value={selectedRefundType}
              onChange={(e) =>
                setSelectedRefundType(e.target.value as RefundType)
              }
            >
              {refundTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>

          {selectedRefundType !== RefundType.ProcessingFee ? (
            <FormControl
              isInvalid={shouldShowAmountError || isRefundAmountZero}
              isRequired
              mb={4}
            >
              <FormLabel>
                Refund Amount (max ${refundRemainingPrice.toFixed(2)})
              </FormLabel>
              <Input
                placeholder="Enter amount"
                value={amount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                type="number"
                step="0.01"
                min="0.01"
                max={refundRemainingPrice}
              />
              {isRefundAmountZero && (
                <FormErrorMessage mt={2}>
                  The refund amount for this type is $0. Nothing to refund.
                </FormErrorMessage>
              )}

              {!isRefundAmountZero && (
                <FormErrorMessage>
                  Please enter a valid amount up to $
                  {refundRemainingPrice.toFixed(2)} with max 2 decimal places.
                </FormErrorMessage>
              )}
            </FormControl>
          ) : (
            <FormControl mb={4} isInvalid={isRefundAmountZero} isRequired>
              <FormLabel>Refunded Processing Fee</FormLabel>
              <Input
                value={`$${refundRemainingPrice.toFixed(2)}`}
                isReadOnly
                variant="filled"
              />
              {isRefundAmountZero && (
                <FormErrorMessage mt={2}>
                  The refund amount for this type is $0. Nothing to refund.
                </FormErrorMessage>
              )}
            </FormControl>
          )}

          <FormControl isInvalid={touchedReason && !reason} isRequired>
            <FormLabel>Refund Reason</FormLabel>
            <Select
              placeholder="Select reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as RefundReason)}
              onBlur={handleReasonBlur}
            >
              {Object.values(RefundReason).map((reasonOption) => (
                <option key={reasonOption} value={reasonOption}>
                  {refundReasonToString(reasonOption)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>
              Please select a reason for the refund.
            </FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleClose} variant={'outline'}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleConfirm}
            variant={'solid'}
            ml={3}
            isDisabled={
              loading ||
              !reason ||
              isRefundAmountZero ||
              (selectedRefundType !== RefundType.ProcessingFee &&
                !isAmountValid())
            }
            isLoading={loading}
          >
            Confirm Refund
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(RefundModal);
